import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Button from "components/button";
import Icon from "components/icon";
import Typography from "components/typography";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import { useFleetServiceV1GetFleetQuery } from "_services/modules/transport-resource/fleet";
import { useTransportOperationServiceV1ListFleetTripHistoryQuery } from "_services/modules/transport-operation/transport-operation";
import { Columns, TableSortState } from "components/table";
import { Loader } from "components/spinner/loader";
import { editFleetPageRouteName, fleetPageRouteName } from "../constants/routes";
import { dateFormatter } from "_helper/timeFormatter";

// Split Components
import DetailInfoCard from "./components/detail-info-card";
import DetailTabsSection from "./components/detail-tabs-section";
import DetailHistorySidebar from "./components/detail-history-sidebar";
import useHistoryPanel from "hooks/shared/useHistoryPanel";

const DetailFleetPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { t } = useAppTranslation("fleet");
  const [activeTab, setActiveTab] = useState(0);

  // Fleet Detail Query
  const { data: detailRes, isLoading } = useFleetServiceV1GetFleetQuery(
    { id: id! },
    { skip: !id }
  );

  const detail = detailRes?.fleet;
  const historyPanel = useHistoryPanel("FLEET");

  // Fleet WO History Query (trip history items are work orders)
  const [woSortState, setWoSortState] = useState<TableSortState>(null);
  const [woCurrentPage, setWoCurrentPage] = useState(1);
  const [woRowsPerPage, setWoRowsPerPage] = useState(10);
  const woOrderByMap: Record<string, string> = {
    wo_number: "wo_number",
    date: "date",
    status: "status",
  };
  const { data: woHistoryRes, isLoading: isWoHistoryLoading, isFetching: isWoHistoryFetching } =
    useTransportOperationServiceV1ListFleetTripHistoryQuery(
      {
        fleetId: id!,
        page: woCurrentPage,
        limit: woRowsPerPage,
        order: woSortState?.direction?.toUpperCase(),
        orderBy: woSortState ? woOrderByMap[woSortState.columnId] : undefined,
      },
      { skip: !id }
    );
  const woTotalPages =
    (woHistoryRes?.metadata as any)?.totalPage ??
    (woHistoryRes?.metadata as any)?.total_page ??
    0;

  const woHistory = useMemo(() => {
    const statusLabels: Record<string, string> = {
      approved: t("status-approved"),
      assigned: t("status-assigned"),
      delivered: t("status-delivered"),
      rejected: t("status-rejected"),
      unassigned: t("status-unassigned"),
      closed: t("status-closed"),
      revision: t("status-revision"),
    };
    return (woHistoryRes?.trip_history || []).map((item) => {
      const rawStatus = (item.status || "").toLowerCase();
      return {
        id: item.id,
        wo_number: item.wo_number || "—",
        customers: item.customer_name || "—",
        date: dateFormatter(item.date) || "—",
        status: statusLabels[rawStatus] || item.status || "—",
      };
    });
  }, [woHistoryRes, t]);

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-detail"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  const woHistoryColumns: Columns<any>[] = useMemo(() => [
    { fieldId: "index", label: t("col-no"), width: "70px" },
    { fieldId: "wo_number", label: t("col-wo-number"), sortable: true },
    { fieldId: "customers", label: t("col-customers") },
    { fieldId: "date", label: t("col-date"), sortable: true },
    { fieldId: "status", label: t("col-status"), sortable: true },
  ], [t]);

  const documentColumns: Columns<any>[] = useMemo(() => [
    { fieldId: "index", label: t("col-no"), width: "70px" },
    { fieldId: "document_type", label: t("label-document-name"), sortable: true },
    { 
      fieldId: "expiry_date", 
      label: t("label-expired-date"), 
      sortable: true,
      render: (row: any) => {
        if (row.document_type === "STNK") return dateFormatter(detail?.stnk_expiry);
        if (row.document_type === "KIR") return dateFormatter(detail?.kir_expiry);
        return "—";
      }
    },
    { 
      fieldId: "file_url", 
      label: t("col-file"), 
      render: (row: any) => (
        row.file_url ? (
          <a
            href={row.file_url}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-primary-600 underline transition-colors hover:text-primary-700"
            data-testid={`btn-view-doc-${(row.document_type || "").toLowerCase()}`}
          >
            {t("btn-open-file") || "Open File"}
          </a>
        ) : (
          <span className="text-text-tertiary">—</span>
        )
      )
    },
  ], [t, detail]);

  const tabs = [
    { id: "to-history", label: t("tab-transport-order-history"), icon: "document-text" as const },
    { id: "documents", label: t("tab-document-list"), icon: "DocumentList" as const },
  ];

  if (isLoading) {
    return (
      <div className="flex min-h-[calc(100vh-200px)] items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <Icon name="warning-polygon" size={48} className="text-error-500" />
        <Typography variant="body" tone="muted">
          {t("error-not-found")}
        </Typography>
        <Button onClick={() => navigate(fleetPageRouteName)} variant="outline">
          {t("btn-back")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[calc(100vh-112px)] -mt-3">
      <DetailInfoCard 
        detail={detail} 
        t={t} 
        onEdit={() => navigate(editFleetPageRouteName(detail.id!), { state: { from: location.state?.from === "draft" ? "draft" : "detail" } })}
        onHistory={() => historyPanel.openPanel(detail.id!, detail.plate_number)}
      />

      <DetailTabsSection
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabs={tabs}
        woHistoryColumns={woHistoryColumns}
        woHistory={woHistory}
        isWoHistoryLoading={isWoHistoryLoading}
        isWoHistoryFetching={isWoHistoryFetching}
        woSortState={woSortState}
        setWoSortState={(sort) => {
          setWoSortState(sort);
          setWoCurrentPage(1);
        }}
        woCurrentPage={woCurrentPage}
        woRowsPerPage={woRowsPerPage}
        woTotalPages={woTotalPages}
        setWoCurrentPage={setWoCurrentPage}
        setWoRowsPerPage={(rows) => {
          setWoRowsPerPage(rows);
          setWoCurrentPage(1);
        }}
        documentColumns={documentColumns}
        documents={detail.documents || []}
        t={t}
      />

      <DetailHistorySidebar
        id={id!}
        isOpen={historyPanel.isOpen}
        onClose={historyPanel.closePanel}
        detail={detail}
      />
    </div>
  );
};

export default DetailFleetPage;
